'use client';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { useEffect, useState, type MouseEvent } from 'react';

type ImageLightboxMediaType = 'image' | 'video';

type ImageLightboxProps = {
  open: boolean;
  src: string | null | undefined;
  alt?: string;
  /** Caption heading shown over the media; omit to hide the title line. */
  title?: string | null;
  /** Optional secondary line shown beneath the title in the caption. */
  subtitle?: string | null;
  /** Renders a video player instead of an image when set to `'video'`. */
  mediaType?: ImageLightboxMediaType;
  /** When set, shows a previous arrow; omit/undefined disables it (at the start). */
  onPrev?: () => void;
  /** When set, shows a next arrow; omit/undefined disables it (at the end). */
  onNext?: () => void;
  onClose: () => void;
};

/**
 * Full-size media viewer for public search animal media. Renders the image (or
 * video, when `mediaType` is `'video'`) centred over a dimmed, blurred backdrop
 * with a fade transition; clicking the backdrop, the close button, or pressing
 * Esc dismisses it. When a `title` (and optional `subtitle`) is supplied it is
 * shown as a caption beneath the media, and the left/right arrow keys move
 * between items while the viewer is open.
 */
export default function ImageLightbox({
  open,
  src,
  alt = 'Animal image',
  title,
  subtitle,
  mediaType = 'image',
  onPrev,
  onNext,
  onClose,
}: ImageLightboxProps) {
  const hasTitle = Boolean(title);
  const hasCaption = hasTitle || Boolean(subtitle);
  const isVideo = mediaType === 'video';
  const showNav = Boolean(onPrev) || Boolean(onNext);

  // Drive prev/next from the keyboard at the document level so navigation works
  // regardless of which control inside the modal currently holds focus.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        onPrev?.();
      } else if (event.key === 'ArrowRight') {
        onNext?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onPrev, onNext]);

  // Shared styling for the circular controls (close / prev / next) that float
  // over the dimmed backdrop.
  const controlSx = {
    color: 'common.white',
    backgroundColor: (theme) => alpha(theme.palette.common.black, 0.45),
    backdropFilter: 'blur(4px)',
    transition: (theme) =>
      theme.transitions.create(['background-color', 'transform']),
    '&:hover': {
      backgroundColor: (theme) => alpha(theme.palette.common.black, 0.7),
      transform: 'scale(1.05)',
    },
    '&.Mui-disabled': {
      color: (theme) => alpha(theme.palette.common.white, 0.3),
    },
  } satisfies SxProps<Theme>;

  // Full-resolution media can take a moment to download; show a spinner and
  // fade the image in once it has loaded. Tracking the loaded source (rather
  // than a boolean) resets the loading state automatically when `src` changes.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isImageLoaded = Boolean(src) && loadedSrc === src;

  // Shared sizing/styling for the image and video so both sit identically over
  // the backdrop.
  const mediaSx = {
    maxWidth: '92vw',
    maxHeight: hasCaption ? '82vh' : '88vh',
    objectFit: 'contain' as const,
    borderRadius: 1,
    display: 'block',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6)',
  };

  return (
    <Modal
      open={open && Boolean(src)}
      onClose={onClose}
      closeAfterTransition
      aria-label={title || alt}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: (theme) => alpha(theme.palette.common.black, 0.65),
            backdropFilter: 'blur(6px)',
          },
        },
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 4 },
      }}
    >
      <Fade in={open && Boolean(src)} timeout={250}>
        <Box
          onClick={onClose}
          sx={{
            position: 'relative',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              ...controlSx,
              position: 'fixed',
              top: { xs: 12, sm: 24 },
              right: { xs: 12, sm: 24 },
            }}
          >
            <CloseIcon />
          </IconButton>
          {src &&
            (isVideo ? (
              <Box
                component="video"
                src={src}
                controls
                preload="metadata"
                // Keep clicks on the player itself from closing the modal.
                onClick={(event: MouseEvent) => event.stopPropagation()}
                sx={{ ...mediaSx, backgroundColor: 'common.black' }}
              />
            ) : (
              <Box
                onClick={(event: MouseEvent) => event.stopPropagation()}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!isImageLoaded && (
                  <CircularProgress
                    aria-label="Loading image"
                    sx={{ position: 'absolute', color: 'common.white' }}
                  />
                )}
                <Box
                  component="img"
                  src={src}
                  alt={alt}
                  onLoad={() => setLoadedSrc(src ?? null)}
                  onError={() => setLoadedSrc(src ?? null)}
                  sx={{
                    ...mediaSx,
                    opacity: isImageLoaded ? 1 : 0,
                    transition: 'opacity 200ms ease',
                  }}
                />
              </Box>
            ))}
          {(hasCaption || showNav) && (
            <Box
              onClick={(event: MouseEvent) => event.stopPropagation()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                maxWidth: '92vw',
              }}
            >
              {showNav && (
                <IconButton
                  onClick={onPrev}
                  disabled={!onPrev}
                  aria-label="Previous"
                  sx={controlSx}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              {hasCaption && (
                <Box
                  sx={{
                    textAlign: 'center',
                    minWidth: 0,
                    px: 2,
                    py: 0.75,
                    borderRadius: subtitle ? 3 : 999,
                    backgroundColor: (theme) =>
                      alpha(theme.palette.common.black, 0.45),
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  {hasTitle && (
                    <Typography
                      sx={{
                        color: 'common.white',
                        fontSize: '1rem',
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                    >
                      {title}
                    </Typography>
                  )}
                  {subtitle && (
                    <Typography
                      sx={{
                        color: (theme) =>
                          alpha(theme.palette.common.white, 0.7),
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              )}
              {showNav && (
                <IconButton
                  onClick={onNext}
                  disabled={!onNext}
                  aria-label="Next"
                  sx={controlSx}
                >
                  <ArrowForwardIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
